import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { format, parseISO } from "date-fns";
import { mdyDow } from "@/lib/dateFmt";
import { HandHelping, Clock, Calendar as CalendarIcon, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Employee-facing dialog for requesting to pick up an open shift.
 *
 * Props:
 *   open, onClose
 *   facilityId, facilityLabel
 *   date       (ISO)
 *   code       (shift code string)
 *   codeMeta   (shift code doc, for label + hours + start/end)
 *   onSubmitted() — refetch schedule + my-requests
 */
export default function PickupRequestDialog({
  open, onClose, facilityId, facilityLabel, date, code, codeMeta, onSubmitted,
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // If the employee already has a PENDING pickup for this exact (date, code)
  // we swap the submit UI for a "Cancel my request" button.
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open || !date || !code) { setExisting(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/shift-pickup-requests/mine");
        if (cancelled) return;
        const match = (data || []).find(
          (r) => r.status === "pending" && r.date === date && r.shift_code === code
                 && r.facility_id === facilityId
        );
        setExisting(match || null);
      } catch { if (!cancelled) setExisting(null); }
    })();
    return () => { cancelled = true; };
  }, [open, date, code, facilityId]);

  if (!open) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/shift-pickup-requests", {
        facility_id: facilityId,
        date,
        shift_code: code,
        reason: reason.trim() || null,
      });
      toast.success("Pickup request sent to your manager");
      setReason("");
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send request");
    }
    setSaving(false);
  };

  const cancelRequest = async () => {
    if (!existing?.id) return;
    setSaving(true);
    try {
      await api.delete(`/shift-pickup-requests/${existing.id}`);
      toast.success("Pickup request cancelled");
      setExisting(null);
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to cancel");
    }
    setSaving(false);
  };

  const timeLine = codeMeta?.start && codeMeta?.end ? `${codeMeta.start} – ${codeMeta.end}` : null;
  const hours = codeMeta?.hours ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="pickup-dialog">
        <div className="p-5 border-b border-slate-200">
          <div className="flex items-center gap-2 font-display font-semibold text-lg">
            <HandHelping size={18} className="text-blue-600" /> Request shift pickup
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            Send this open shift to your manager for approval. You&apos;ll see the outcome under My Requests.
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <span className="inline-flex items-center rounded-md border border-blue-300 bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-bold" data-testid="pickup-code">
                {code}
              </span>
              <span className="text-slate-700">{codeMeta?.label || "Open shift"}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1"><CalendarIcon size={12} />{mdyDow(date)}</span>
              {timeLine && <span className="inline-flex items-center gap-1"><Clock size={12} />{timeLine}</span>}
              <span className="tabular-nums font-medium text-slate-700">{hours}h</span>
              {facilityLabel && <span className="text-slate-500">· {facilityLabel}</span>}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Reason (optional)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1"
              placeholder="e.g. Happy to cover — free that day"
              data-testid="pickup-reason"
              disabled={!!existing}
            />
          </div>

          {existing ? (
            <div className="text-[11px] text-fuchsia-800 flex items-start gap-1.5 rounded-md bg-fuchsia-50 border border-fuchsia-200 p-2" data-testid="pickup-existing-banner">
              <HandHelping size={12} className="mt-0.5 text-fuchsia-700 shrink-0" />
              You already requested this shift — awaiting your manager. Cancel below if you changed your mind.
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 p-2">
              <AlertTriangle size={12} className="mt-0.5 text-amber-700 shrink-0" />
              Your manager will see this in the Requests tab with an automatic overtime check for your schedule.
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {existing ? (
            <Button
              variant="outline"
              className="border-fuchsia-400 text-fuchsia-800 hover:bg-fuchsia-50"
              onClick={cancelRequest} disabled={saving}
              data-testid="pickup-cancel-request"
            >
              <XCircle size={14} className="mr-1.5" />
              {saving ? "Cancelling…" : "Cancel my request"}
            </Button>
          ) : (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} disabled={saving} data-testid="pickup-submit">
              {saving ? "Sending…" : "Send request"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
